import { API } from 'api';
import { apiBasePath, apiName } from 'assets/constants';

import { DOMAINS, IMPORTANCE, SECTIONS } from '@repo/monitoring';

export type ConditionMetricCounts = {
  condition_id: number;
  condition: string;
  condition_display_name: string;
  good: number;
  okay: number;
  improve: number;
  support: number;
};

export type ConditionWithActionItems = {
  condition_id: number;
  condition: string;
  condition_display_name: string;
  sensitive: boolean;
  good: number;
  okay: number;
  improve: number;
  support: number;
  action_items_count: number;
};

export type ConditionMetricRange = {
  range_lower: number;
  range_upper: number;
  evaluation_color: string;
};

export type SingleConditionMetric = {
  metric_id: number;
  name: string;
  data_type: string;
  evaluation_color: string;
  user_value: number;
  health_metrics_barscale_percentage: number;
  // Sourced from key_insights_category/section, not gvh's legacy columns.
  // section = section display title; section_order = category order within it.
  section: string | null;
  section_order: number | null;
  section_priority: number | null;
  ranges?: ConditionMetricRange[];
  evaluation_texts: string | null;
  evaluation_text: string | null;
};

export type SingleConditionData = {
  condition_id: number;
  condition: string;
  condition_display_name: string;
  about: string;
  faq: string;
  references: string;
  sensitive: boolean;
  marked_in_survey: boolean;
  metrics: SingleConditionMetric[];
};

// Optimized endpoints
const getConditionMetricCounts = async (kitId: string, limit: number = 2) => {
  const response = await API.get(
    apiName,
    `${apiBasePath}/conditions/metrics/${kitId}/summary?limit=${limit}`,
    {},
    true,
    undefined,
    {
      tags: {
        section: SECTIONS.RESULTS,
        domain: DOMAINS.CONDITIONS,
        importance: IMPORTANCE.CRITICAL,
      },
      context: {
        kitId,
        limit,
      },
    },
  );
  return response.result as ConditionMetricCounts[];
};

const getConditionMetricsList = async (kitId: string) => {
  const response = await API.get(
    apiName,
    `${apiBasePath}/conditions/metrics/${kitId}/totals`,
    {},
    true,
    undefined,
    {
      tags: {
        section: SECTIONS.RESULTS,
        domain: DOMAINS.CONDITIONS,
        importance: IMPORTANCE.CRITICAL,
      },
      context: {
        kitId,
      },
    },
  );
  return response.result as ConditionWithActionItems[];
};

const getSingleConditionMetrics = async (
  kitId: string,
  conditionName: string,
) => {
  const response = await API.get(
    apiName,
    `${apiBasePath}/conditions/metrics/${kitId}/${conditionName}`,
    {},
    true,
    undefined,
    {
      tags: {
        section: SECTIONS.RESULTS,
        domain: DOMAINS.CONDITIONS,
        importance: IMPORTANCE.CRITICAL,
      },
      context: {
        kitId,
        conditionName,
      },
    },
  );
  return response.result as SingleConditionData;
};

export const getConditionMetricCountsQuery = (
  kitId?: string,
  limit: number = 2,
) => ({
  queryKey: [apiName, 'getConditionMetricCounts', kitId, limit],
  queryFn: () => (kitId ? getConditionMetricCounts(kitId, limit) : []),
});

export const getConditionMetricsListQuery = (kitId?: string) => ({
  queryKey: [apiName, 'getConditionMetricsList', kitId],
  queryFn: () => (kitId ? getConditionMetricsList(kitId) : []),
});

export const getSingleConditionMetricsQuery = (
  kitId?: string,
  conditionName?: string,
) => ({
  queryKey: [apiName, 'getSingleConditionMetrics', kitId, conditionName],
  queryFn: () =>
    kitId && conditionName
      ? getSingleConditionMetrics(kitId, conditionName)
      : null,
  enabled: !!kitId && !!conditionName,
});
